import { useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { MoreHorizontal, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { PageHeader } from "@/components/shared/page-header";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney } from "@/features/clients/constants";
import { useClients } from "@/features/clients/api";
import { useDeleteQuotation, useQuotations } from "@/features/quotations/api";
import { QT_STATUSES, labelFor, statusTone } from "@/features/quotations/constants";
import type { QuotationListRow, QuotationStatus } from "@/features/quotations/types";
import { isSupabaseConfigured } from "@/lib/supabase";

export function QuotationsPage({ clientId: clientIdProp, embedded }: { clientId?: string; embedded?: boolean } = {}) {
  return (
    <ErrorBoundary title="Quotations crashed">
      <QuotationsInner clientIdProp={clientIdProp} embedded={embedded} />
    </ErrorBoundary>
  );
}

function QuotationsInner({
  clientIdProp,
  embedded,
}: {
  clientIdProp?: string;
  embedded?: boolean;
}) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { client?: string };
  const clientFilter = clientIdProp ?? search.client ?? "all";

  const { data = [], isLoading, error, refetch, isFetching } = useQuotations(
    clientFilter !== "all" ? { clientId: clientFilter } : undefined,
  );
  const { data: clients = [] } = useClients();
  const remove = useDeleteQuotation();

  const [statusFilter, setStatusFilter] = useState<"all" | QuotationStatus>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState<QuotationListRow | null>(null);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return data.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (from && row.issue_date < from) return false;
      if (to && row.issue_date > to) return false;
      if (!query) return true;
      return [row.qt_number, row.client_name, row.client_code]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [data, statusFilter, from, to, q]);

  const kpis = useMemo(() => {
    const open = data.filter((r) => ["draft", "sent"].includes(r.status));
    return {
      draft: data.filter((r) => r.status === "draft").length,
      openValue: open.reduce((s, r) => s + r.grand_total, 0),
      accepted: data.filter((r) => r.status === "accepted").length,
    };
  }, [data]);

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to use quotations."
      />
    );
  }

  return (
    <div className="space-y-3">
      {!embedded ? (
        <PageHeader
          title="Quotations"
          description="Client price offers · GST · convert to SO in Phase 4.2"
          actions={
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isFetching}
                onClick={() => refetch()}
              >
                <RefreshCw className={isFetching ? "animate-spin" : ""} />
                Refresh
              </Button>
              <Button type="button" size="sm" onClick={() => navigate({ to: "/quotations/new" })}>
                <Plus />
                New quotation
              </Button>
            </>
          }
        />
      ) : null}

      {!embedded ? (
        <div className="flex flex-wrap items-end gap-2 text-xs">
          <div className="rounded border bg-card px-3 py-2">
            <span className="text-muted-foreground">Draft</span>{" "}
            <strong className="tabular-nums">{kpis.draft}</strong>
          </div>
          <div className="rounded border bg-card px-3 py-2">
            <span className="text-muted-foreground">Open pipeline</span>{" "}
            <strong className="tabular-nums">{formatMoney(kpis.openValue)}</strong>
          </div>
          <div className="rounded border bg-card px-3 py-2">
            <span className="text-muted-foreground">Accepted</span>{" "}
            <strong className="tabular-nums">{kpis.accepted}</strong>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-8 pl-7 text-xs"
            placeholder="Search QT no, client…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {embedded || clientIdProp ? null : (
          <NativeSelect
            className="h-8 w-auto text-xs"
            value={clientFilter}
            disabled={Boolean(clientIdProp)}
            onChange={(e) =>
              navigate({
                to: "/quotations",
                search: e.target.value === "all" ? {} : { client: e.target.value },
              })
            }
          >
            <option value="all">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.name}
              </option>
            ))}
          </NativeSelect>
        )}
        <NativeSelect
          className="h-8 w-auto text-xs"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
        >
          <option value="all">All statuses</option>
          {QT_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </NativeSelect>
        <Input type="date" className="h-8 w-36 text-xs" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Input type="date" className="h-8 w-36 text-xs" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>

      {isLoading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <EmptyState title="Failed to load" description={(error as Error).message} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No quotations"
          description="Raise your first quotation for a client."
          action={
            <Button size="sm" onClick={() => navigate({ to: "/quotations/new" })}>
              <Plus />
              New quotation
            </Button>
          }
        />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>QT #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Issue</TableHead>
                <TableHead>Valid until</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Lines</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row) => (
                <TableRow key={row.id} className="text-xs">
                  <TableCell>
                    <Link
                      to="/quotations/$qtId"
                      params={{ qtId: row.id }}
                      className="font-mono font-medium hover:underline"
                    >
                      {row.qt_number}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-muted-foreground">{row.client_code}</span>{" "}
                    {row.client_name}
                  </TableCell>
                  <TableCell>{format(parseISO(row.issue_date), "dd MMM yyyy")}</TableCell>
                  <TableCell>
                    {row.valid_until ? format(parseISO(row.valid_until), "dd MMM yyyy") : "—"}
                  </TableCell>
                  <TableCell>
                    <StatusDot tone={statusTone(row.status)}>
                      {labelFor(QT_STATUSES, row.status)}
                    </StatusDot>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.line_count ?? 0}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatMoney(row.grand_total)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm">
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to="/quotations/$qtId" params={{ qtId: row.id }}>
                            Open
                          </Link>
                        </DropdownMenuItem>
                        {row.status === "draft" ? (
                          <>
                            <DropdownMenuItem asChild>
                              <Link to="/quotations/$qtId/edit" params={{ qtId: row.id }}>
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleting(row)}
                            >
                              <Trash2 />
                              Delete draft
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete draft quotation?"
        description={`${deleting?.qt_number} will be permanently removed.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}
